import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { MapPin, Mail, Phone, Clock, Send, CheckCircle } from "lucide-react";
import axios from "axios";
import { slideInLeft, slideInRight, fadeUpEnter } from "@/constants/animations";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const sujets = [
  "Demande d'information",
  "Transport routier",
  "Fret maritime",
  "Logistique & stockage",
  "Partenariat",
  "Autre",
];

const INITIAL_FORM = { nom: "", email: "", fonction: "", telephone: "", sujet: sujets[0], message: "" };

const INPUT_CLS = "w-full bg-white/[0.04] border border-white/10 text-white placeholder-slate-600 px-4 py-3 rounded-xl text-sm focus:border-[#E8902A]/50 focus:bg-white/[0.06] transition-all duration-200";

// --- Sub-components ---

function ContactInfoPanel() {
  return (
    <motion.div {...slideInLeft} className="lg:col-span-2 space-y-6">
      <div>
        <h2 className="font-heading font-black text-2xl text-white mb-2 uppercase">Nos coordonnées</h2>
        <p className="text-slate-400 text-sm">Nous répondons à tous vos messages rapidement.</p>
      </div>

      <ul className="space-y-4">
        <li className="flex items-start gap-3 text-slate-400 text-sm">
          <div className="w-10 h-10 rounded-lg bg-[#E8902A]/10 flex items-center justify-center flex-shrink-0">
            <MapPin size={18} className="text-[#E8902A]" />
          </div>
          <div>
            <div className="font-heading font-bold text-white text-sm mb-1">Adresse</div>
            <div className="text-slate-400 text-sm">27 Avenue de Gramont<br />03200 Vichy, France</div>
          </div>
        </li>

        <li className="flex items-start gap-4 p-4 rounded-xl border border-white/5 bg-white/[0.02]">
          <div className="w-10 h-10 rounded-lg bg-[#E8902A]/10 flex items-center justify-center flex-shrink-0">
            <Mail size={18} className="text-[#E8902A]" />
          </div>
          <div>
            <div className="font-heading font-bold text-white text-sm mb-1">Email</div>
            <a href="mailto:pierre@pierreaffretement.fr" className="text-slate-400 text-sm hover:text-[#E8902A] transition-colors" data-testid="contact-email">
              pierre@pierreaffretement.fr
            </a>
          </div>
        </li>

        <li className="flex items-start gap-4 p-4 rounded-xl border border-white/5 bg-white/[0.02]">
          <div className="w-10 h-10 rounded-lg bg-[#E8902A]/10 flex items-center justify-center flex-shrink-0">
            <Phone size={18} className="text-[#E8902A]" />
          </div>
          <div>
            <div className="font-heading font-bold text-white text-sm mb-1">Téléphone</div>
            <a href="tel:+33443500252" className="text-slate-400 text-sm hover:text-[#E8902A] transition-colors" data-testid="contact-phone">
              +33 (0)4 43 50 02 52
            </a>
          </div>
        </li>

        <li className="flex items-start gap-4 p-4 rounded-xl border border-white/5 bg-white/[0.02]">
          <div className="w-10 h-10 rounded-lg bg-[#E8902A]/10 flex items-center justify-center flex-shrink-0">
            <Clock size={18} className="text-[#E8902A]" />
          </div>
          <div>
            <div className="font-heading font-bold text-white text-sm mb-1">Horaires</div>
            <div className="text-slate-400 text-sm">Lundi – Vendredi<br />8h30–12h | 14h–17h30</div>
          </div>
        </li>
      </ul>

      <div className="p-5 rounded-xl border border-[#E8902A]/20 bg-[#E8902A]/5">
        <div className="font-heading font-bold text-[#E8902A] text-sm mb-1 uppercase tracking-wide">Réponse rapide garantie</div>
        <p className="text-slate-400 text-xs leading-relaxed">
          Pour toute demande urgente, précisez-le dans votre message. Nous traitons les urgences en priorité.
        </p>
      </div>

      <div className="rounded-xl overflow-hidden border border-white/10" style={{ height: "220px" }}>
        <iframe
          title="Pierre Affrètement - 27 Avenue Gramont, Vichy"
          src="https://maps.google.com/maps?q=27+Avenue+Gramont+03200+Vichy+France&t=&z=15&ie=UTF8&iwloc=&output=embed"
          width="100%"
          height="220"
          style={{ border: 0, filter: "invert(90%) hue-rotate(180deg)" }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          data-testid="contact-map"
        />
      </div>
    </motion.div>
  );
}

function ContactFormPanel({ form, status, handleChange, handleSubmit, onReset }) {
  return (
    <motion.div {...slideInRight} className="lg:col-span-3">
      {status === "success" ? (
        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="contact-success">
          <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mb-5">
            <CheckCircle size={32} className="text-emerald-400" />
          </div>
          <h3 className="font-heading font-black text-2xl text-white mb-3 uppercase">Message envoyé !</h3>
          <p className="text-slate-400 max-w-sm">
            Merci pour votre message. Notre équipe vous répondra dans les plus brefs délais.
          </p>
          <button onClick={onReset} className="mt-6 text-[#E8902A] font-heading font-bold text-sm tracking-widest uppercase hover:underline">
            Envoyer un autre message
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Nom complet *</label>
              <input type="text" name="nom" value={form.nom} onChange={handleChange} required placeholder="Jean Dupont" className={INPUT_CLS} data-testid="contact-nom" />
            </div>
            <div>
              <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Email *</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="jean@entreprise.fr" className={INPUT_CLS} data-testid="contact-email-input" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Votre fonction</label>
              <input type="text" name="fonction" value={form.fonction} onChange={handleChange} placeholder="Responsable logistique" className={INPUT_CLS} data-testid="contact-fonction" />
            </div>
            <div>
              <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Numéro de téléphone</label>
              <input type="tel" name="telephone" value={form.telephone} onChange={handleChange} placeholder="0470 00 00 00" className={INPUT_CLS} data-testid="contact-telephone" />
            </div>
          </div>

          <div>
            <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Sujet *</label>
            <select name="sujet" value={form.sujet} onChange={handleChange} required className="w-full bg-[#1a2e48] border border-white/10 text-white px-4 py-3 rounded-xl text-sm focus:border-[#E8902A]/50 transition-all duration-200 appearance-none" data-testid="contact-sujet">
              {sujets.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Message *</label>
            <textarea name="message" value={form.message} onChange={handleChange} required rows={6} placeholder="Décrivez votre besoin..." className="w-full bg-white/[0.04] border border-white/10 text-white placeholder-slate-600 px-4 py-3 rounded-xl text-sm focus:border-[#E8902A]/50 focus:bg-white/[0.06] transition-all duration-200 resize-none" data-testid="contact-message" />
          </div>

          {status === "error" && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="contact-error">
              Une erreur est survenue. Veuillez réessayer.
            </div>
          )}

          <button
            type="submit"
            disabled={status === "loading"}
            className="flex items-center gap-2 bg-[#E8902A] hover:bg-[#C26218] disabled:opacity-60 disabled:cursor-not-allowed text-white font-heading font-bold text-sm tracking-widest uppercase px-8 py-4 transition-all duration-300 shadow-[0_4px_20px_rgba(232,144,42,0.35)] hover:shadow-[0_6px_28px_rgba(232,144,42,0.5)] hover:-translate-y-0.5"
            data-testid="contact-submit"
          >
            {status === "loading" ? "Envoi en cours..." : "Envoyer le message"}
            {status !== "loading" && <Send size={16} />}
          </button>
        </form>
      )}
    </motion.div>
  );
}

// --- Page principale ---

export default function Contact() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState("idle");

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm(INITIAL_FORM);
    } catch {
      setStatus("error");
    }
  };

  return (
    <div data-testid="contact-page">
      <Helmet>
        <title>Contact — Pierre Affrètement | Vichy (03)</title>
        <link rel="canonical" href="https://pierreaffretement.fr/contact" />
        <meta name="description" content="Contactez Pierre Affrètement, commissionnaire de transport à Vichy (03). Réponse sous 24h." />
      </Helmet>

      {/* Hero */}
      <section className="relative py-32 pt-44 bg-[#1a2e48] overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/contact-bg.png" alt="Contact" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a2e48]/50 to-[#1a2e48]" />
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#E8902A]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUpEnter}>
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10 bg-[#E8902A]" />
              <span className="font-heading font-bold text-xs tracking-widest uppercase text-[#E8902A]">Contactez-nous</span>
            </div>
            <h1 className="font-heading font-black text-5xl md:text-6xl text-white leading-[0.95] uppercase mb-4">
              Parlons de <span className="gradient-text">votre projet</span>
            </h1>
            <p className="text-slate-400 text-lg max-w-xl leading-relaxed">
              Notre équipe à Vichy est disponible du lundi au vendredi pour répondre à toutes vos questions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-[#152540]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <ContactInfoPanel />
            <ContactFormPanel
              form={form}
              status={status}
              handleChange={handleChange}
              handleSubmit={handleSubmit}
              onReset={() => setStatus("idle")}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
